import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { PageShell } from "@/components/PageShell";
import { Loader2, CheckCircle2, XCircle, Copy, Sparkles, Mail } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BilanConfirmation() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { t, locale } = useLang();
  const isFr = locale === "fr";
  const [state, setState] = useState({ loading: true, paid: false, code: null, err: "", analysis: null, analysisStatus: null, masterBypass: false });

  useEffect(() => {
    if (!sid) {
      setState({ loading: false, paid: false, code: null, err: isFr ? "Session manquante" : "Missing session", analysis: null, analysisStatus: null, masterBypass: false });
      return;
    }
    let attempts = 0;
    const maxAttempts = 40; // ~80s total
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/bilan/status/${sid}`);
        if (r.data.payment_status === "paid") {
          const analysis = r.data.analysis || null;
          const status = r.data.analysis_status || (analysis ? "ready" : "pending");
          const masterBypass = !!r.data.master_bypass;
          // Wait for analysis to be ready for ALL users (master + real payers).
          // Both flows generate the analysis asynchronously now.
          const isReady = !!analysis;
          setState({
            loading: !isReady,
            paid: true,
            code: r.data.client_code || null,
            err: "",
            analysis,
            analysisStatus: status,
            masterBypass,
          });
          if (isReady) return;
        } else if (r.data.session_status === "expired") {
          setState({ loading: false, paid: false, code: null, err: isFr ? "Session expirée" : "Session expired", analysis: null, analysisStatus: null, masterBypass: false });
          return;
        }
      } catch (e) { /* swallow and retry */ }
      attempts++;
      if (attempts < maxAttempts) setTimeout(poll, 2000);
      else setState((s) => ({ ...s, loading: false, err: s.err || (isFr ? "Analyse en cours, elle vous sera envoyée par email sous 48h." : "Analysis pending — you will receive it by email within 48h.") }));
    };
    poll();
  }, [sid, isFr]);

  return (
    <PageShell
      testId="page-bilan-confirmation"
      title={isFr ? "Confirmation du Bilan" : "Bilan Confirmation"}
    >
      <div className="glass glow-border rounded-3xl p-8 md:p-12 max-w-4xl">
        {state.loading && (
          <div className="flex items-center gap-3 text-slate-300" data-testid="bilan-conf-loading">
            <Loader2 className="animate-spin text-[#00F0FF]" />{" "}
            {state.paid
              ? (isFr ? "Génération de votre analyse personnalisée et de vos 9 recettes — quelques instants…" : "Generating your personalised analysis and your 9 recipes — just a moment…")
              : (isFr ? "Vérification du paiement Stripe…" : "Verifying Stripe payment…")}
          </div>
        )}

        {!state.loading && state.paid && (
          <div data-testid="bilan-conf-success">
            <div className="flex items-center gap-3 text-[#00F0FF] mb-4">
              <CheckCircle2 size={28} />
              <span className="font-display text-2xl text-amber-glow">
                {state.masterBypass
                  ? (isFr ? "Accès propriétaire confirmé" : "Owner access confirmed")
                  : (isFr ? "Paiement confirmé" : "Payment confirmed")}
              </span>
            </div>
            <p className="text-slate-300 leading-relaxed mb-4">
              {isFr
                ? "Voici votre Bilan complet : profilage selon les 5 sous-piliers FACOW360 et vos 9 recettes personnalisées (3 repas × 3 niveaux : Cantine, Chef, Chef Étoilé). Une copie identique vous a été envoyée par e-mail."
                : "Here is your full Bilan: profiling along the 5 FACOW360 sub-pillars and your 9 personalised recipes (3 meals × 3 levels: Canteen, Chef, Star Chef). An identical copy has been sent to your email."}
            </p>
            <p className="inline-flex items-center gap-2 text-xs text-[#D4AF37] mb-6 font-mono-tech tracking-widest uppercase">
              <Mail size={13} /> {isFr ? "Email envoyé" : "Email sent"}
            </p>

            <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6 mb-6">
              <Sparkles className="text-[#D4AF37] mb-3" />
              <p className="label-overline mb-2">{isFr ? "Votre code client unique" : "Your unique client code"}</p>
              <div className="font-display text-3xl md:text-4xl text-amber-glow tracking-[0.2em] mb-3 select-all" data-testid="bilan-client-code">
                {state.code || "—"}
              </div>
              {state.code && (
                <button onClick={() => navigator.clipboard.writeText(state.code)} className="btn-primary !px-4 !py-2 !text-xs" data-testid="bilan-copy-code">
                  <Copy size={12} /> {isFr ? "Copier le code" : "Copy code"}
                </button>
              )}
              <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                {isFr
                  ? "Conservez précieusement ce code : il vous identifie auprès de FACOW360 pour tout suivi futur (relances, abonnement Cuisine Vivante, recharges, prestations physiques)."
                  : "Keep this code safe: it identifies you with FACOW360 for any future follow-up (reminders, Living Cuisine subscription, recharges, on-site services)."}
              </p>
            </div>

            {state.analysis && (
              <div className="rounded-2xl border border-[#00F0FF]/25 bg-[#0A1128]/70 p-6 md:p-8 mb-6" data-testid="bilan-analysis-block">
                <p className="label-overline mb-4 text-[#00F0FF]">
                  {isFr ? "— Votre Bilan complet" : "— Your complete Bilan"}
                </p>
                <div className="bilan-analysis prose prose-invert max-w-none">
                  <ReactMarkdown>{state.analysis}</ReactMarkdown>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <Link to="/cuisine-vivante" className="btn-gold" data-testid="bilan-conf-cta-cuisine">
                {isFr ? "Découvrir la Cuisine Vivante" : "Discover Living Cuisine"}
              </Link>
              <Link to="/" className="btn-primary" data-testid="bilan-conf-cta-home">
                {isFr ? "Retour à l'accueil" : "Back to home"}
              </Link>
            </div>
          </div>
        )}

        {!state.loading && !state.paid && (
          <div data-testid="bilan-conf-error">
            <div className="flex items-center gap-3 text-red-400 mb-4">
              <XCircle size={28} />
              <span className="font-display text-2xl">
                {isFr ? "Paiement non confirmé" : "Payment not confirmed"}
              </span>
            </div>
            <p className="text-slate-400 mb-6">{state.err || ""}</p>
            <Link to="/bilan" className="btn-primary" data-testid="bilan-conf-retry">
              {isFr ? "Reprendre mon bilan" : "Restart my assessment"}
            </Link>
          </div>
        )}

        <p className="mt-8 text-xs text-slate-500">{t("disclaimer")}</p>
      </div>
    </PageShell>
  );
}
